import React, { useEffect, useRef } from 'react';
import { Heart, InstagramIcon, LinkedinIcon, ExternalLink, Mail, Twitter, Linkedin, Github, XIcon, TwitterIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Logo } from '../Navbar/Logo';
import { trackButtonClick } from '../../firebaseConfig';
import { useAnalyticsTracking } from '../../hooks/useAnalyticsTracking';

export function Footer() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { trackButtonClick: trackAnalyticsButton, trackNavigation, trackSectionView } = useAnalyticsTracking();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Track section view
            trackSectionView('Footer Section', {
              section_name: 'footer',
              section_position: 'bottom',
              visibility_percentage: Math.round(entry.intersectionRatio * 100)
            });
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [trackSectionView]);

  const handleSocialClick = (platform: string, url: string) => {
    // Comprehensive analytics tracking
    trackAnalyticsButton(`${platform} Social Link`, 'Footer', {
      button_type: 'social_link',
      button_position: 'footer_social',
      social_platform: platform,
      utm_source: 'footer',
      utm_medium: 'social_link',
      utm_campaign: `social_${platform.toLowerCase()}`
    });
    
    // Keep existing Firebase tracking for backward compatibility
    trackButtonClick(`${platform} Social Link`, 'Footer');
    
    window.open(url, '_blank');
  };

  return (
    <footer 
      ref={sectionRef}
      className="relative py-6 overflow-hidden bg-background-light dark:bg-background-dark"
    >
      {/* Background Effects - with pointer-events-none to not block clicks */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-light/5 to-transparent dark:via-primary-dark/5 pointer-events-none" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Left Side */}
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8">
            {/* Logo */}
            <Logo isExpanded={false} isCentered={false} />
            
            {/* Links */}
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
              <Link 
                to="/privacy" 
                className="text-sm text-text-secondary-light dark:text-text-secondary-dark hover:text-primary-light dark:hover:text-primary-dark transition-colors duration-300"
              >
                Privacy
              </Link>
              <Link 
                to="/terms" 
                className="text-sm text-text-secondary-light dark:text-text-secondary-dark hover:text-primary-light dark:hover:text-primary-dark transition-colors duration-300"
              >
                Terms of Service
              </Link>
              <a 
                href="mailto:support@mocktrail.app" 
                className="text-sm text-text-secondary-light dark:text-text-secondary-dark hover:text-primary-light dark:hover:text-primary-dark transition-colors duration-300"
              >
                Support
              </a>
            </div>
          </div>

          {/* Right Side */}
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-6">
            {/* Social Links */}
            <div className="flex flex-row items-center gap-4 mb-1 md:mb-0">
              {/* Instagram */}
              <button 
                onClick={() => handleSocialClick('Instagram', 'https://www.instagram.com/mocktrail.ai')}
                className="cursor-pointer"
              >
                <InstagramIcon className="w-4 h-4 text-text-secondary-light dark:text-text-secondary-dark hover:text-primary-light dark:hover:text-primary-dark transition-colors duration-300" />
              </button>
              {/* LinkedIn */}
              <button 
                onClick={() => handleSocialClick('LinkedIn', 'https://www.linkedin.com/company/mocktrail')}
                className="cursor-pointer"
              >
                <LinkedinIcon className="w-4 h-4 text-text-secondary-light dark:text-text-secondary-dark hover:text-primary-light dark:hover:text-primary-dark transition-colors duration-300" />
              </button>
              {/* X (Twitter) */}
              <button 
                onClick={() => handleSocialClick('X', 'https://x.com/mocktrail_ai')}
                className="cursor-pointer"
              >
                <TwitterIcon className="w-4 h-4 text-text-secondary-light dark:text-text-secondary-dark hover:text-primary-light dark:hover:text-primary-dark transition-colors duration-300" />
              </button>
            </div>
            {/* Made in India */}
            <p className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark flex items-center gap-1">
              <img src={"https://upload.wikimedia.org/wikipedia/commons/b/bc/Flag_of_India.png"} alt="Made in India" className="w-4 h-3 inline-block mr-1" />
              <span className="text-sm text-orange-500 font-heading ">Made</span> <span className="text-sm text-text-primary-light dark:text-text-primary-dark font-heading">in</span> <span className="text-sm text-green-500 font-heading">India</span>
            </p>

            {/* Made with Love */}
            <div className="relative group">
              {/* Glow effect */}
              <div className="absolute -inset-2 bg-gradient-to-r from-primary-light via-secondary-light/50 to-primary-light dark:from-primary-dark dark:via-secondary-dark/50 dark:to-primary-dark opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-500 pointer-events-none" />
              
              <p className="relative text-sm text-text-secondary-light dark:text-text-secondary-dark whitespace-nowrap">
                Made with{' '}
                <span className="inline-block align-middle mx-1 text-accent-light dark:text-accent-dark animate-pulse">
                  <Heart className="w-3 h-3 fill-current" />
                </span>
                {' '}by{' '}
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark font-medium hover:cursor-pointer">
                  Aurivance Tech.
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}